<template>
    <form-group
        v-bind="form_group_props"
    >
        <template v-slot:read_only>
            <slot name="read_only">
                <input :disabled="true" :value="selected_value" class="uk-width-1-1" />
            </slot>
        </template>
        <template v-slot:label><slot name="label"></slot></template>
        <template v-slot:hint><slot name="hint"></slot></template>

        <div :class="{'uk-open': options_displayed }" class="uk-autocomplete uk-width-1-1">
            <input
                ref="field"
                :id="identifier"
                :name="name"
                v-model="editor_value"
                :placeholder="placeholder"
                :disabled="disabled"
                autocomplete="off"
                :class="{ 'uk-form-warning': (required && !editor_value), 'uk-form-danger': !!error, [`uk-width-${width}`]: true, [`uk-form-${size}`]: true }"
                class="uk-width-1-1"
                @focus="focused"
                @blur="blurred"
                @keyup="update_options"
                @keyup.enter = 'enter'
                @keyup.down = 'down'
                @keyup.up = 'up'
                @keyup.escape = 'blurred'
            >
            <div v-show="editable&&options_displayed"  class="uk-dropdown uk-dropdown-autocomplete">
                <ul class="uk-nav uk-nav-autocomplete uk-autocomplete-results">
                    <li v-if="!available_options.length&&!add_option" class="uk-text-muted">
                        {{ $root.translate('No results found...') }}
                    </li>
                    <li v-else v-for="(option, index) in available_options" :key="option[idField]" :class="{'uk-active': (current === index)}">
                        <a href @click.prevent="pick(option)">
                            <slot name="option" :option="option">
                                <span v-html="highlighted_label(option)"></span>
                            </slot>
                        </a>
                    </li>
                    <li v-if="!!add_option&&!!available_options.length" class="uk-nav-divider"></li>
                    <li v-if="!!add_option">
                        <a href @click.prevent="pick(add_option)">ADD {{ add_option[labelField] }}</a>
                    </li>
                </ul>
            </div>
        </div>
    </form-group>
</template>

<script>
import debounce from "lodash/debounce"
import orderBy from "lodash/orderBy"

import is_nibnut_component from "../mixins/IsNibnutComponent"

import FormGroup from "./FormGroup"

let blur_hack_timer

export default {
    name: "FormAutosuggest",
    mixins: [is_nibnut_component],
    components: {
        FormGroup
    },
    mounted () {
        this.maybe_reset_editor_value()
        this.set_search()
    },
    beforeDestroy () {
        this.deactivate()
    },
    watch: {
        selection: "maybe_reset_editor_value",
        options: "options_changed",
        externalSearch: "set_search"
    },
    methods: {
        set_search () {
            if(this.externalSearch) this.update_options({ target: { value: this.externalSearch } })
        },
        maybe_reset_editor_value () {
            if(!this.activated && (this.editor_value !== this.selected_value)) this.editor_value = this.selected_value
        },
        options_changed () {
            if(this.selection && this.options) {
                const selected_option = this.options.find(option => option[this.idField] === this.selection)
                if(!selected_option || (selected_option[this.labelField] !== this.editor_value)) this.maybe_reset_editor_value()
            }
        },
        focused () {
            this.activate()
            if(this.displayOnFocus) this.options_displayed = true
            if(this.displayOnEmpty) this.$emit("search", this.editor_value)
        },
        blurred () {
            if(blur_hack_timer) {
                clearTimeout(blur_hack_timer)
                blur_hack_timer = null
            }
            blur_hack_timer = setTimeout(() => {
                this.deactivate()
            }, 150)
        },
        update_options: debounce(function (event) {
            this.editor_value = event.target.value
            if(!this.displayOnFocus) this.options_displayed = this.editor_value_long_enough
            if(!this.editor_value) this.unpick()
            if(this.editor_value_long_enough) this.$emit("search", this.editor_value)
            else this.$emit("search", "")
        }, 300),
        activate () {
            if(this.editable && !this.activated) {
                document.body.addEventListener("touchend", this.maybe_deactivate, { passive: false, capture: false })
                document.body.addEventListener("click", this.maybe_deactivate, { passive: false, capture: false })

                setTimeout(() => {
                    this.$refs.field.focus()
                    this.update_options({ target: { value: this.editor_value } })
                }, 150)
                this.activated = true
            }
        },
        deactivate () {
            if(blur_hack_timer) {
                clearTimeout(blur_hack_timer)
                blur_hack_timer = null
            }
            document.body.removeEventListener("touchend", this.maybe_deactivate, false)
            document.body.removeEventListener("click", this.maybe_deactivate, false)
            if(this.activated) this.activated = false
            this.options_displayed = false
            if(this.supports_multiple_options) this.editor_value = ""
            else this.editor_value = this.selected_value
        },
        maybe_deactivate (event) {
            const container = event.target.closest(".uk-form-row")
            if(this.activated && (!container || (this.$el !== container))) this.deactivate()
        },
        highlighted_label (option) {
            let label = ""
            if(option) label = option[this.labelField] || `${option}`
            if(label && this.editor_value) {
                const length = this.editor_value.length
                const index = label.toLowerCase().indexOf(this.editor_value.toLowerCase())
                if(index >= 0) label = `${label.substring(0, index)}<strong class="uk-text-lam">${label.substring(index, index + length)}</strong>${label.substring(index + length)}`
            }
            return label
        },
        pick (option) {
            if(this.editable) {
                this.$emit("pick", option)
                this.deactivate()
            }
        },
        unpick (option) {
            this.$emit("unpick", option)
        },
        case_insensitive_index_of (string, substring) {
            if(!string || !substring) return -1
            return `${string}`.toLowerCase().indexOf(`${substring}`.toLowerCase())
        },
        enter () {
            if((this.current >= 0) && (this.current < this.available_options.length)) {
                this.pick(this.available_options[this.current])
            }
        },
        up () {
            this.current--
            if(this.current < 0) this.current = this.available_options.length - 1
        },
        down () {
            this.current++
            if(this.current >= this.available_options.length) this.current = 0
        }
    },
    computed: {
        form_group_props () {
            return {
                id: this.id,
                name: this.name,
                value: this.value,
                required: this.required,
                editable: this.editable,
                error: this.error,
                waiting: this.saving
            }
        },
        available_options () {
            const option_filter = option => {
                // if(!!option[this.idField] && (option[this.idField] === this.selected_value)) return true
                return !!option[this.labelField] && (!this.editor_value || (this.case_insensitive_index_of(option[this.labelField], this.editor_value) >= 0))
            }
            return orderBy(this.options.filter(option => {
                if(this.supports_multiple_options) {
                    return (this.selection.indexOf(option[this.idField]) < 0) && option_filter(option)
                }
                return option_filter(option)
            }), ["name"], ["asc"])
        },
        selected_options () {
            if(this.supports_multiple_options) {
                return this.selection.map(id => {
                    return this.options.find(option => option[this.idField] === id)
                })
            }
            const option = this.options.find(option => option[this.idField] === this.selection)
            return option ? [option] : []
        },
        selected_value () {
            let selected_option
            if(this.selection && this.options) selected_option = this.options.find(option => option[this.idField] === this.selection)
            if(selected_option) return selected_option[this.labelField]
            return this.selection ? this.selection : ""
        },
        editor_value_long_enough () {
            return this.displayOnEmpty || (!!this.editor_value && (this.editor_value.length >= 3))
            // return this.displayOnEmpty || (!!this.editor_value && ((this.editor_value.length >= 3) || (typeof this.editor_value === "number") || (this.editor_value.match(/^\d+$/))))
        },
        add_option () {
            if(!!this.editable && !!this.canAdd && !!this.editor_value && !!this.editor_value_long_enough && !this.available_options.find(option => option[this.labelField].toLowerCase() === this.editor_value.toLowerCase())) {
                return { id: 0, [this.labelField]: this.editor_value }
            }
            return null
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        options: {
            type: Array,
            default () {
                return []
            }
        },
        selection: {
            default: null
        },
        externalSearch: {
            type: String,
            default: ""
        },
        emptyValue: {
            default: 0
        },
        emptyLabel: {
            type: String,
            default: ""
        },
        idField: {
            type: String,
            default: "id"
        },
        labelField: {
            type: String,
            default: "name"
        },
        placeholder: {
            type: String,
            default: ""
        },
        width: {
            type: String,
            default: "1-1"
        },
        size: {
            type: String,
            validator: prop => !!prop && !!prop.match(/^(small|normal|large)$/i),
            default: "normal"
        },
        required: {
            type: Boolean,
            required: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        canAdd: {
            type: Boolean,
            default: false
        },
        displayOnFocus: {
            type: Boolean,
            default: true
        },
        displayOnEmpty: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ""
        }
    },
    data () {
        return {
            supports_multiple_options: false,
            activated: false,
            options_displayed: false,
            editor_value: "",
            current: -1
        }
    }
}
</script>
